/**
 * The single source of truth for "which meal slot is this log in".
 *
 * A `MealLog`'s `mealType` is optional, and the two nutrition screens used to
 * disagree about what a null means: the overview folded it into `SNACK`, the
 * detail screen into `BREAKFAST`, so the same log appeared under a different slot
 * on each. **Snack is the canonical answer** — the business requirements state it
 * directly ("the day screen already renders untyped logs under Snack").
 *
 * **This is a read-side projection only.** It changes where a log is *shown*,
 * never what is *stored*: the copy planner still preserves a null `mealType`
 * verbatim, so a null-slot log stays null after a copy and simply renders under
 * Snack on both screens.
 *
 * Generic over `{ mealType }` rather than typed against the PowerSync schema, so
 * it works against every screen's narrower row type and stays inside the `unit`
 * jest project's constraints (no `@/` mapper, no `jest.mock`).
 */

/** The four real slots, mirroring the generated `MealType` enum. */
export type MealSlot = 'BREAKFAST' | 'LUNCH' | 'DINNER' | 'SNACK';

/** The minimum shape slot-grouping needs. */
export interface MealTypeBearing {
  mealType?: string | null;
}

const ALL_SLOTS: MealSlot[] = ['BREAKFAST', 'LUNCH', 'DINNER', 'SNACK'];

export class MealSlotGrouper {
  /** Where a log with no meal slot belongs. */
  static readonly UNTYPED_SLOT: MealSlot = 'SNACK';

  /** Every slot, in display order. */
  static readonly SLOTS: readonly MealSlot[] = ALL_SLOTS;

  /** The slot a log renders under — its own, or `UNTYPED_SLOT` when it has none. */
  static slotOf(log: MealTypeBearing): MealSlot {
    return (
      (log.mealType as MealSlot | null | undefined) ??
      MealSlotGrouper.UNTYPED_SLOT
    );
  }

  /**
   * Groups logs by slot, always returning **all four** keys (empty arrays
   * included) so a caller can render an empty slot without a null check.
   * Input order is preserved within each slot.
   */
  static groupBySlot<T extends MealTypeBearing>(
    logs: T[],
  ): Record<MealSlot, T[]> {
    const grouped = Object.fromEntries(
      ALL_SLOTS.map((slot) => [slot, [] as T[]]),
    ) as Record<MealSlot, T[]>;
    for (const log of logs) {
      grouped[MealSlotGrouper.slotOf(log)].push(log);
    }
    return grouped;
  }

  /** The logs that render under `slot` — including null-slot logs when `slot` is Snack. */
  static inSlot<T extends MealTypeBearing>(logs: T[], slot: string): T[] {
    return logs.filter((log) => MealSlotGrouper.slotOf(log) === slot);
  }
}
